import type { RealtimeChannel } from "@supabase/supabase-js";
import { PHASE, PLAYER } from "../../common/constants.ts";
import type { MpLiveGame, TradeEventTeams } from "../../common/types.ts";
import { draft, player, team } from "../core/index.ts";
import { getNumPlayersTradedAwayNormalizedAll } from "../core/player/getNumPlayersTradedAwayNormalized.ts";
import { idb } from "../db/index.ts";
import { g, helpers, logEvent, toUI } from "../util/index.ts";
import { recomputeLocalUITeamOvrs } from "../util/recomputeLocalUITeamOvrs.ts";
import { kvGet, kvRemove, kvSet } from "./authStorage.ts";
import {
	getClient,
	getLeagueIdForLid,
	getLobbyState,
	getLocalLid,
	getSession,
	simDirtyKey,
} from "./index.ts";
import { importSnapshotToLeague } from "./importSnapshot.ts";
import { isImportingSnapshot, registerLeagueRepairer } from "./importLock.ts";
// Cycles (these modules import syncNow from here) - safe, deref at call time
import { ensureAssetsSeeded } from "./trades.ts";
import { maybeAutoResolveFa } from "./fa.ts";
import { applyFiringEvent } from "./firing.ts";
import { applyLineupEvent } from "./lineup.ts";
import { applyProtectEvent } from "./expansion.ts";
import {
	continueDraftIfAuto,
	ensureDraftReconcile,
	processDraftPick,
	stopDraftReconcile,
} from "./simRunner.ts";

// ============================================================================
// The sync engine: every state change in a multiplayer league is an event in
// the league_events table. Clients apply events from the log IN ORDER -
// including events they created themselves - so every client runs exactly one
// deterministic code path and converges to identical state. Anything random
// is pre-rolled by the event's creator and shipped in the payload.
// ============================================================================

export type MpTradeAsset = {
	kind: "player" | "pick";
	id: number;
	from_tid: number;
	to_tid: number;
	jerseyNumber?: string;
};

export type MpTradeEventPayload = {
	tids: [number, number];
	assets: MpTradeAsset[];
	eventTeams: TradeEventTeams;
	phase: number;
	season: number;
};

type MpEventRow = {
	seq: number;
	league_id: string;
	type: string;
	payload: any;
	created_by: string | null;
};

let connectedLeagueId: string | undefined;
let channel: RealtimeChannel | undefined;
let syncChain: Promise<unknown> = Promise.resolve();
// Phase 8b: a spectator's accumulated live-game play-by-play feed (from the
// runner's broadcast). Held here so we can append across broadcast frames.
let spectatorLiveGame: MpLiveGame | undefined;

const seqKey = (leagueId: string) => `mpSeq:${leagueId}`;

export const getLastSeq = async (leagueId: string) => {
	const raw = await kvGet(seqKey(leagueId));
	return raw === null ? 0 : Number(raw);
};

// Record that all events up to seq are reflected in local state (used when
// local state comes from a snapshot rather than replaying events)
export const markSeqApplied = async (leagueId: string, seq: number) => {
	await kvSet(seqKey(leagueId), String(seq));
};

// Phase 7b: rebuild the local league from the canonical Supabase state -
// re-import the latest snapshot over the existing league, then replay any
// events published since it. Supabase is the source of truth; this discards a
// diverged or corrupted local cache.
export const rebuildFromServer = async (leagueId: string) => {
	const { league: mpLeague } = await getLobbyState(leagueId);
	if (!mpLeague.snapshot_path) {
		throw new Error("This league has no snapshot to rebuild from yet.");
	}
	const lid = await getLocalLid(leagueId);
	await importSnapshotToLeague({
		leagueId,
		snapshotPath: mpLeague.snapshot_path,
		importLid: lid,
		conditions: {},
	});
	await markSeqApplied(leagueId, mpLeague.snapshot_seq ?? 0);
	invalidateMpLidCache();
	// Apply anything published after the snapshot
	await syncNow(leagueId);
	await toUI("realtimeUpdate", [["playerMovement", "multiplayer"]]);
};

// Self-healing (see importLock.ts): a local league DB that fails to open is
// disposable - Supabase has the canonical snapshot - so rebuild it in place
// instead of leaving the league permanently unopenable. Returns false when
// the lid isn't a multiplayer league (or the rebuild itself fails) so the
// original open error surfaces to the user instead.
registerLeagueRepairer(async (lid: number) => {
	let leagueId;
	try {
		leagueId = await getLeagueIdForLid(lid);
	} catch {
		return false;
	}
	if (leagueId === undefined) {
		return false;
	}
	try {
		logEvent({
			type: "info",
			text: "Local league data was damaged - restoring it from the server...",
			saveToDb: false,
			showNotification: true,
		});
		await rebuildFromServer(leagueId);
		return true;
	} catch (error) {
		console.error("Multiplayer league repair failed", error);
		return false;
	}
});

export const getConnectedLeagueId = () => connectedLeagueId;

// Fast check used by the guards that disable single-player-only features
const mpLidCache = new Map<number, boolean>();
export const isMultiplayerLeague = async () => {
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		return false;
	}
	if (!mpLidCache.has(lid)) {
		mpLidCache.set(lid, (await getLeagueIdForLid(lid)) !== undefined);
	}
	return mpLidCache.get(lid)!;
};
export const invalidateMpLidCache = () => {
	mpLidCache.clear();
};

// ============================================================================
// Event application
// ============================================================================

// Deterministic mirror of core/trade/processTrade.ts + the accepted branch of
// core/trade/propose.ts, with all randomness (jersey numbers) taken from the
// payload instead of rolled locally.
const applyTradeEvent = async (payload: MpTradeEventPayload) => {
	const duringSeason = payload.phase <= PHASE.PLAYOFFS;

	const playerInfos: {
		p: any;
		fromTid: number;
	}[] = [];

	let maxPlayerValue = -Infinity;
	let maxPid: number | undefined;

	for (const asset of payload.assets) {
		if (asset.kind === "player") {
			const p = await idb.cache.players.get(asset.id);
			if (!p) {
				throw new Error(`Trade event references unknown player ${asset.id}`);
			}
			p.tid = asset.to_tid;
			p.ptModifier = 1;

			if (duringSeason && asset.jerseyNumber !== undefined) {
				player.setJerseyNumber(p, asset.jerseyNumber);
			}

			if (p.valueFuzz > maxPlayerValue) {
				maxPlayerValue = p.valueFuzz;
				maxPid = p.pid;
			}

			playerInfos.push({ p, fromTid: asset.from_tid });
			await idb.cache.players.put(p);

			const teamSeason = await idb.cache.teamSeasons.indexGet(
				"teamSeasonsBySeasonTid",
				[payload.season, asset.from_tid],
			);
			if (teamSeason) {
				teamSeason.numPlayersTradedAway += helpers.sigmoid(
					p.valueNoPot / 100,
					30,
					0.47,
				);
				await idb.cache.teamSeasons.put(teamSeason);
			}
		} else {
			const dp = await idb.cache.draftPicks.get(asset.id);
			if (!dp) {
				throw new Error(`Trade event references unknown pick ${asset.id}`);
			}
			dp.tid = asset.to_tid;
			await idb.cache.draftPicks.put(dp);
		}
	}

	// News event, best player's photo first (same as processTrade)
	let pidsEvent = payload.assets
		.filter((a) => a.kind === "player")
		.map((a) => a.id);
	const dpidsEvent = payload.assets
		.filter((a) => a.kind === "pick")
		.map((a) => a.id);
	if (maxPid !== undefined) {
		pidsEvent = [maxPid, ...pidsEvent.filter((pid) => pid !== maxPid)];
	}

	const eid = await logEvent({
		type: "trade",
		showNotification: false,
		pids: pidsEvent,
		dpids: dpidsEvent,
		tids: payload.tids,
		score: Math.round(
			helpers.bound(maxPlayerValue - 40, 0, Number.POSITIVE_INFINITY),
		),
		teams: payload.eventTeams,
		phase: payload.phase as any,
	});

	for (const { p, fromTid } of playerInfos) {
		if (!p.transactions) {
			p.transactions = [];
		}
		p.transactions.push({
			season: payload.season,
			phase: payload.phase,
			tid: p.tid,
			type: "trade",
			fromTid,
			eid,
		});
		await idb.cache.players.put(p);
	}

	// Roster auto-sort, same rules as the accepted branch of trade.propose
	for (const tid of payload.tids) {
		const t = await idb.cache.teams.get(tid);
		const onlyNewPlayers =
			g.get("userTids").includes(tid) &&
			!g.get("spectator") &&
			t &&
			!t.keepRosterSorted;
		await team.rosterAutoSort(tid, onlyNewPlayers);
	}
};

// v16.2: the commissioner undid a trade (accidental trade). Deterministic
// inverse of applyTradeEvent - every asset moves back to the team that gave it
// up. The checksum only covers player.tid + pick.tid, so reversing those makes
// clients byte-identical again; the rest (ptModifier reset, the trade
// transaction entry, numPlayersTradedAway) is restored for cleanliness.
const applyTradeUndoEvent = async (payload: {
	undoes_seq: number;
	tids: [number, number];
	assets: MpTradeAsset[];
	phase: number;
	season: number;
}) => {
	for (const asset of payload.assets) {
		if (asset.kind === "player") {
			const p = await idb.cache.players.get(asset.id);
			if (!p) {
				continue;
			}
			// Back to the team that traded him away
			p.tid = asset.from_tid;
			p.ptModifier = 1;

			// Drop the trade transaction this reverses (kept deterministic - every
			// client has the same transactions array; not in the checksum)
			if (p.transactions) {
				for (let i = p.transactions.length - 1; i >= 0; i--) {
					const tx = p.transactions[i]!;
					if (
						tx.type === "trade" &&
						(tx as any).fromTid === asset.from_tid &&
						tx.season === payload.season
					) {
						p.transactions.splice(i, 1);
						break;
					}
				}
			}
			await idb.cache.players.put(p);

			// Reverse the numPlayersTradedAway bump (no sim between trade and undo,
			// so valueNoPot is unchanged and this cancels exactly)
			const teamSeason = await idb.cache.teamSeasons.indexGet(
				"teamSeasonsBySeasonTid",
				[payload.season, asset.from_tid],
			);
			if (teamSeason) {
				teamSeason.numPlayersTradedAway = Math.max(
					0,
					teamSeason.numPlayersTradedAway -
						helpers.sigmoid(p.valueNoPot / 100, 30, 0.47),
				);
				await idb.cache.teamSeasons.put(teamSeason);
			}
		} else {
			const dp = await idb.cache.draftPicks.get(asset.id);
			if (dp) {
				dp.tid = asset.from_tid;
				await idb.cache.draftPicks.put(dp);
			}
		}
	}

	for (const tid of payload.tids) {
		const t = await idb.cache.teams.get(tid);
		const onlyNewPlayers =
			g.get("userTids").includes(tid) &&
			!g.get("spectator") &&
			t &&
			!t.keepRosterSorted;
		await team.rosterAutoSort(tid, onlyNewPlayers);
	}

	logEvent({
		type: "info",
		text: "A trade was undone by the commissioner.",
		saveToDb: false,
		showNotification: true,
	});
};

// Deterministic mirror of core/player/sign.ts for auction winners. No jersey
// randomness: sign() skips jersey assignment outside the season, and free
// agency is always after the playoffs.
const applyFaRoundEvent = async (payload: {
	round: number;
	phase: number;
	season: number;
	signings: {
		pid: number;
		tid: number;
		amount: number;
		exp: number;
		name: string;
	}[];
	final: boolean;
}) => {
	for (const signing of payload.signings) {
		const p = await idb.cache.players.get(signing.pid);
		if (!p) {
			throw new Error(`FA event references unknown player ${signing.pid}`);
		}
		if (p.tid !== PLAYER.FREE_AGENT) {
			throw new Error(
				`FA event signing for player ${signing.pid} who is not a free agent (tid ${p.tid})`,
			);
		}

		const isRookie =
			p.stats.length === 0 &&
			p.draft.year === payload.season &&
			p.draft.tid === signing.tid;

		p.tid = signing.tid;
		p.numDaysFreeAgent = 0;
		p.gamesUntilTradable = isRookie ? 0 : Math.round(0.17 * g.get("numGames"));
		delete p.numPlayersTradedAwayNormalized;

		player.setContract(p, { amount: signing.amount, exp: signing.exp }, true);

		const score = Math.round(
			helpers.bound(p.valueFuzz - 45, 0, Number.POSITIVE_INFINITY),
		);
		const eid = await logEvent({
			type: "freeAgent",
			showNotification: false,
			pids: [p.pid],
			tids: [p.tid],
			score,
			contract: p.contract,
		});

		if (!p.transactions) {
			p.transactions = [];
		}
		p.transactions.push({
			season: payload.season,
			phase: payload.phase as any,
			tid: p.tid,
			type: "freeAgent",
			eid,
		});

		await idb.cache.players.put(p);

		const t = await idb.cache.teams.get(signing.tid);
		const onlyNewPlayers =
			g.get("userTids").includes(signing.tid) &&
			!g.get("spectator") &&
			t &&
			!t.keepRosterSorted;
		await team.rosterAutoSort(signing.tid, onlyNewPlayers);
	}

	logEvent({
		type: "info",
		text: payload.final
			? "The free agency auction has ended."
			: `Free agency round ${payload.round} resolved: ${payload.signings.length} player(s) signed.`,
		saveToDb: false,
		showNotification: true,
	});
};

// Deterministic mirror of core/player/sign.ts for a re-signing. The player is
// currently a free agent (newPhaseResignPlayers moved expiring players to free
// agency with a re-sign negotiation); re-signing puts them back on their team.
const applyResignEvent = async (payload: {
	pid: number;
	tid: number;
	amount: number;
	exp: number;
	phase: number;
	season: number;
}) => {
	const p = await idb.cache.players.get(payload.pid);
	if (!p) {
		throw new Error(`Resign event references unknown player ${payload.pid}`);
	}

	p.tid = payload.tid;
	p.numDaysFreeAgent = 0;
	p.gamesUntilTradable = 0; // re-signing your own player
	delete p.numPlayersTradedAwayNormalized;

	player.setContract(p, { amount: payload.amount, exp: payload.exp }, true);

	// Same as core/player/sign.ts: re-signings log a reSigned event but (unlike
	// free-agent signings) do not append a player transaction row
	await logEvent({
		type: "reSigned",
		showNotification: false,
		pids: [p.pid],
		tids: [p.tid],
		score: Math.round(
			helpers.bound(p.valueFuzz - 45, 0, Number.POSITIVE_INFINITY),
		),
		contract: p.contract,
	});

	await idb.cache.players.put(p);

	// Remove the now-resolved re-sign negotiation on every client
	const negotiation = await idb.cache.negotiations.get(payload.pid);
	if (negotiation) {
		await idb.cache.negotiations.delete(payload.pid);
	}

	const t = await idb.cache.teams.get(payload.tid);
	const onlyNewPlayers =
		g.get("userTids").includes(payload.tid) &&
		!g.get("spectator") &&
		t &&
		!t.keepRosterSorted;
	await team.rosterAutoSort(payload.tid, onlyNewPlayers);
};

// v17: the runner broadcast the picks made since the last pause instead of a
// full league snapshot. draft.selectPlayer has no randomness in the annual
// draft, so replaying the selections in order lands every client on the same
// state - at the cost of a few row writes rather than a database rebuild.
// The runner skips its own event (it already applied these locally), and the
// snapshot published when the draft ends re-converges everyone regardless.
const applyDraftPickEvent = async (payload: {
	selections: {
		dpid: number;
		tid: number;
		round: number;
		pick: number;
		originalTid: number;
		pid: number;
	}[];
	on_clock: number | null;
}) => {
	for (const s of payload.selections ?? []) {
		const p = await idb.cache.players.get(s.pid);
		if (!p || p.tid !== PLAYER.UNDRAFTED) {
			// Already applied (our own optimistic pick, or a replayed event).
			// Never throw: one odd selection must not wedge the whole importer.
			continue;
		}
		await draft.selectPlayer(
			{
				dpid: s.dpid,
				tid: s.tid,
				round: s.round,
				pick: s.pick,
				originalTid: s.originalTid,
				season: g.get("season"),
			} as any,
			s.pid,
		);
	}
	// false: the runner decides when the draft is actually over, and publishes a
	// real snapshot at that point
	await draft.afterPicks(false, {});
};

// A human signed a free agent outside the auction (first-come-first-serve,
// like normal BBGM). Deterministic mirror of core/player/sign.ts, applied by
// every client. First-come-first-serve is enforced at the RPC (the ledger
// claim is atomic, so only one signing event is ever appended per player), but
// this still no-ops if the player is somehow no longer a free agent - never
// throw, or an unrelated importer would wedge on it.
const applySigningEvent = async (payload: {
	pid: number;
	tid: number;
	amount: number;
	exp: number;
	phase: number;
	season: number;
	name: string;
}) => {
	const p = await idb.cache.players.get(payload.pid);
	if (!p || p.tid !== PLAYER.FREE_AGENT) {
		return;
	}

	p.tid = payload.tid;
	p.numDaysFreeAgent = 0;
	p.gamesUntilTradable = Math.round(0.17 * g.get("numGames"));
	delete p.numPlayersTradedAwayNormalized;

	player.setContract(p, { amount: payload.amount, exp: payload.exp }, true);

	const score = Math.round(
		helpers.bound(p.valueFuzz - 45, 0, Number.POSITIVE_INFINITY),
	);
	const eid = await logEvent({
		type: "freeAgent",
		showNotification: false,
		pids: [p.pid],
		tids: [p.tid],
		score,
		contract: p.contract,
	});

	if (!p.transactions) {
		p.transactions = [];
	}
	p.transactions.push({
		season: payload.season,
		phase: payload.phase as any,
		tid: p.tid,
		type: "freeAgent",
		eid,
	});

	await idb.cache.players.put(p);

	const t = await idb.cache.teams.get(payload.tid);
	const onlyNewPlayers =
		g.get("userTids").includes(payload.tid) &&
		!g.get("spectator") &&
		t &&
		!t.keepRosterSorted;
	await team.rosterAutoSort(payload.tid, onlyNewPlayers);

	logEvent({
		type: "info",
		text: `${payload.name} signed with the ${g.get("teamInfoCache")[payload.tid]?.name ?? "team"}.`,
		saveToDb: false,
		showNotification: true,
	});
};

// v16: a member released one of their own players. Mirrors core/player/release
// (dead money recorded, player becomes a free agent), applied by every client
// so the release is deterministic and survives the next sim.
// RETIRED FEATURE - reader only. The commissioner mood tool that produced
// 'mood_edit' events was removed, and nothing can create new ones. This stays
// because events already in a league's log are still replayed: rebuildFromServer
// (and the self-healing repair, and any late join) re-imports the last snapshot
// and applies everything published after it, so deleting this reader would
// silently drop those adjustments on rebuild and diverge that client from the
// rest of the league. Once a checkpoint publishes with the adjustment baked into
// the snapshot, this can go too.
//
// Deterministic: the payload carries the whole list, so every client ends up
// with the identical array and a replay is a no-op rather than stacking the
// bonus again.
const applyMoodEditEvent = async (payload: {
	pid: number;
	name: string;
	items: { amount: number; text: string; tid?: number }[];
}) => {
	const p = await idb.cache.players.get(payload.pid);
	if (!p) {
		return;
	}

	if (payload.items.length > 0) {
		p.customMoodItems = payload.items;
	} else {
		delete p.customMoodItems;
	}
	await idb.cache.players.put(p);

	await toUI("realtimeUpdate", [["playerMovement"]]);
};

const applyReleaseEvent = async (payload: {
	pid: number;
	tid: number;
	contract: { amount: number; exp: number };
	addDeadMoney: boolean;
	justDrafted: boolean;
	phase: number;
	season: number;
}) => {
	const p = await idb.cache.players.get(payload.pid);
	// Stale-event safety: only release a player still on that team
	if (!p || p.tid !== payload.tid) {
		return;
	}

	// Dead money: the old team keeps paying his contract until it would expire
	// (getPayroll / getContracts count releasedPlayers against the cap).
	if (payload.addDeadMoney) {
		await idb.cache.releasedPlayers.add({
			pid: p.pid,
			tid: payload.tid,
			contract: {
				amount: payload.contract.amount,
				exp: payload.contract.exp,
			},
		} as any);
	}

	if (payload.justDrafted) {
		// Just-drafted players are released with no salary owed
		p.salaries = [];
	}

	const teamName = g.get("teamInfoCache")[payload.tid]?.name ?? "team";
	logEvent({
		type: "release",
		text: `The ${teamName} released <a href="${helpers.leagueUrl([
			"player",
			p.pid,
		])}">${p.firstName} ${p.lastName}</a>.`,
		showNotification: false,
		pids: [p.pid],
		tids: [payload.tid],
	});

	// Becomes a free agent; keeps his contract as his asking demand (it decays
	// over the regular season via decreaseDemands). addToFreeAgents is fully
	// deterministic (no RNG), so every client lands on identical state - it
	// also resets ptModifier and stamps numPlayersTradedAwayNormalized exactly
	// like a vanilla release.
	await player.addToFreeAgents(p, await getNumPlayersTradedAwayNormalizedAll());
	await idb.cache.players.put(p);
};

const applyEvent = async (event: MpEventRow) => {
	if (event.type === "trade") {
		await applyTradeEvent(event.payload);
	} else if (event.type === "trade_undo") {
		await applyTradeUndoEvent(event.payload);
	} else if (event.type === "resign") {
		await applyResignEvent(event.payload);
	} else if (event.type === "firing") {
		await applyFiringEvent(event.payload);
		invalidateMpLidCache();
	} else if (event.type === "checkpoint") {
		// Legacy Phase 3 events (before synced sim existed): clock moved with no
		// league mutation
		logEvent({
			type: "info",
			text: `The league advanced to the next checkpoint: <b>${event.payload?.to ?? "?"}</b>. Move budgets have reset.`,
			saveToDb: false,
			showNotification: true,
		});
	} else if (event.type === "fa_round") {
		await applyFaRoundEvent(event.payload);
	} else if (event.type === "signing") {
		await applySigningEvent(event.payload);
	} else if (event.type === "release") {
		await applyReleaseEvent(event.payload);
	} else if (event.type === "mood_edit") {
		// Retired feature; kept so historical events still replay (see above)
		await applyMoodEditEvent(event.payload);
	} else if (event.type === "protect") {
		// v16: a member chose which players to protect from the expansion draft
		await applyProtectEvent(event.payload);
	} else if (event.type === "draft_pick") {
		const session = await getSession();
		if (event.created_by === session?.userId) {
			// We are the runner who made these picks - already applied locally
			return;
		}
		await applyDraftPickEvent(event.payload);
	} else if (event.type === "lineup") {
		// v11: a member locked in their roster order / playing time /
		// play-through-injury at ready-up. Applied by every client (creator
		// included) so the runner's sim uses it.
		await applyLineupEvent(event.payload);
	} else if (event.type === "sim") {
		const session = await getSession();
		if (event.created_by === session?.userId) {
			// We are the runner who published this - our local state IS the
			// canonical snapshot, no need to re-import it
			return;
		}
		const isPlayoff = event.payload?.playoffs === true;
		const isDraft = event.payload?.draft === true;
		const isExpansion = event.payload?.expansion === true;
		logEvent({
			type: "info",
			text: isDraft
				? "Updating draft board..."
				: isPlayoff
					? "Downloading playoff results..."
					: "Downloading simulation results...",
			saveToDb: false,
			showNotification: true,
		});
		const lid = await getLocalLid(event.league_id);
		try {
			await importSnapshotToLeague({
				leagueId: event.league_id,
				snapshotPath: event.payload.snapshot_path,
				importLid: lid,
				conditions: {},
			});
		} catch (error) {
			// A failed import can leave a half-created league DB (empty
			// gameAttributes -> "Undefined season" forever). Retrying recreates it
			// from scratch (importLid deletes the broken DB first), which
			// self-heals transient failures like a mid-import refresh or network
			// blip.
			console.error("Snapshot import failed, retrying once:", error);
			// Debug breadcrumb: SharedWorker console output is invisible to the
			// page, so keep the last import failure readable via evalOnWorker
			(globalThis as any).mpLastImportError =
				`${new Date().toISOString()} ${(error as Error).stack ?? (error as Error).message}`.slice(
					0,
					1000,
				);
			logEvent({
				type: "error",
				text: `Snapshot import failed (${error.message}) - retrying...`,
				saveToDb: false,
			});
			await importSnapshotToLeague({
				leagueId: event.league_id,
				snapshotPath: event.payload.snapshot_path,
				importLid: lid,
				conditions: {},
			});
		}
		invalidateMpLidCache();
		// Phase 8a: the authoritative results are in now, so clear the live ticker
		// (in case the ephemeral live_done broadcast was missed).
		void toUI("updateLocal", [{ mpLiveScores: undefined }]);
		logEvent({
			type: "info",
			text: isDraft
				? "Draft board updated. If it's your pick, go to the Draft page to select a player."
				: isExpansion
					? "<b>Expansion draft!</b> New teams are joining the league. Protect your players on the Protect Players page, then ready up to continue."
					: isPlayoff
						? "Playoff results downloaded. If your team is still alive, ready up to continue."
						: `Simulation results downloaded - the league is now at <b>${event.payload?.to ?? "?"}</b>. Trade budgets have reset.`,
			saveToDb: false,
			showNotification: true,
		});
	} else {
		// Unknown event types come from newer protocol phases; the version gate
		// on join should prevent this, but don't corrupt state if it happens.
		throw new Error(
			`Unknown multiplayer event type "${event.type}" (seq ${event.seq}) - update your game?`,
		);
	}
};

// ============================================================================
// Catch-up and realtime
// ============================================================================

const doSync = async (leagueId: string) => {
	if (connectedLeagueId !== leagueId) {
		return;
	}

	const lastSeq = await getLastSeq(leagueId);
	const { data, error } = await getClient()
		.from("league_events")
		.select()
		.eq("league_id", leagueId)
		.gt("seq", lastSeq)
		.order("seq");
	if (error) {
		throw new Error(`Sync fetch failed: ${error.message}`);
	}

	const events = (data ?? []) as MpEventRow[];

	// Skip-ahead: events before a sim snapshot are already baked into it, so
	// jump straight to the newest snapshot instead of replaying stale trades
	// against pre-sim state.
	let startIndex = 0;
	for (let i = events.length - 1; i >= 0; i--) {
		if (events[i]!.type === "sim") {
			startIndex = i;
			break;
		}
	}
	if (startIndex > 0) {
		await kvSet(seqKey(leagueId), String(events[startIndex - 1]!.seq));
	}

	for (let i = startIndex; i < events.length; i++) {
		const event = events[i]!;
		await applyEvent(event);
		await kvSet(seqKey(leagueId), String(event.seq));
	}

	if (events.length > 0) {
		await idb.cache.flush();
		await recomputeLocalUITeamOvrs();
		await toUI("realtimeUpdate", [
			["playerMovement", "gameSim", "newPhase", "multiplayer"],
		]);
	}
};

// Serialized so overlapping triggers (realtime + navigation) can't apply the
// same event twice.
export const syncNow = (leagueId: string) => {
	const next = syncChain.then(() => doSync(leagueId));
	syncChain = next.catch((error) => {
		console.error("Multiplayer sync error:", error);
		// Debug breadcrumb (see the import catch above)
		(globalThis as any).mpLastSyncError =
			`${new Date().toISOString()} ${(error as Error).stack ?? (error as Error).message}`.slice(
				0,
				1000,
			);
		logEvent({
			type: "error",
			text: `Multiplayer sync error: ${error.message}`,
			saveToDb: false,
			persistent: true,
		});
	});
	return next;
};

export const disconnectLeagueSync = async () => {
	if (channel) {
		const c = channel;
		channel = undefined;
		await getClient().removeChannel(c);
	}
	connectedLeagueId = undefined;
	spectatorLiveGame = undefined;
	stopDraftReconcile();
	await toUI("updateLocal", [{ mpLeague: false, mpLiveGame: undefined }]);
};

// Phase 8a: send an ephemeral broadcast on the league-sync channel (live
// scores). Best-effort and fire-and-forget - a dropped frame just means a
// skipped ticker update; the canonical state always comes from the snapshot.
export const broadcastToChannel = (event: string, payload: unknown) => {
	if (!channel) {
		return;
	}
	void channel.send({ type: "broadcast", event, payload });
};

// Called on every league view (from beforeView). Idempotent and cheap when
// already connected to the right league.
export const connectLeagueSync = async (lid: number | undefined) => {
	if (lid === undefined) {
		if (connectedLeagueId) {
			await disconnectLeagueSync();
		}
		return;
	}

	const leagueId = await getLeagueIdForLid(lid);
	if (!leagueId) {
		if (connectedLeagueId) {
			await disconnectLeagueSync();
		}
		return;
	}
	if (connectedLeagueId === leagueId) {
		return;
	}
	await disconnectLeagueSync();

	connectedLeagueId = leagueId;
	await toUI("updateLocal", [{ mpLeague: true }]);

	const client = getClient();
	const { data } = await client.auth.getSession();
	if (data.session) {
		await client.realtime.setAuth(data.session.access_token);
	}

	// If this client was mid-simulation as the runner and the canonical state
	// moved on without it (crash, demotion, published elsewhere), its local
	// league has unpublished dice rolls - restore from the official snapshot.
	try {
		if ((await kvGet(simDirtyKey(leagueId))) !== null) {
			const { league: mpLeague } = await getLobbyState(leagueId);
			const lastSeq = await getLastSeq(leagueId);
			const superseded =
				!mpLeague.sim_open || (mpLeague.snapshot_seq ?? 0) > lastSeq;
			if (superseded && mpLeague.snapshot_path) {
				logEvent({
					type: "error",
					text: "Your unpublished simulation was superseded - restoring the official league state...",
					saveToDb: false,
					showNotification: true,
				});
				await importSnapshotToLeague({
					leagueId,
					snapshotPath: mpLeague.snapshot_path,
					importLid: lid,
					conditions: {},
				});
				await markSeqApplied(leagueId, mpLeague.snapshot_seq ?? 0);
				await kvRemove(simDirtyKey(leagueId));
				invalidateMpLidCache();
			}
		}
	} catch (error) {
		console.error("Dirty-sim check failed:", error);
	}

	// Arm the runner's draft safety-net poll on connect, not only when a
	// leagues-row UPDATE arrives. A stalled draft produces no updates at all,
	// so a runner who reloads the page would otherwise never start the poll -
	// which is exactly the situation the poll exists to recover from.
	try {
		const { league: mpLeague } = await getLobbyState(leagueId);
		const session = await getSession();
		if (
			mpLeague.draft_active &&
			session?.userId === (mpLeague.sim_runner ?? mpLeague.commissioner)
		) {
			ensureDraftReconcile(leagueId);
		}
	} catch {
		// Offline or transient - the realtime handler arms it on the next update
	}

	channel = client
		.channel(`league-sync-${leagueId}`)
		.on(
			"postgres_changes",
			{
				event: "INSERT",
				schema: "public",
				table: "league_events",
				filter: `league_id=eq.${leagueId}`,
			},
			() => {
				void syncNow(leagueId);
			},
		)
		.on(
			"postgres_changes",
			{
				event: "*",
				schema: "public",
				table: "trade_offers",
				filter: `league_id=eq.${leagueId}`,
			},
			(change) => {
				void onOfferChange(leagueId, change);
			},
		)
		.on(
			"postgres_changes",
			{
				event: "*",
				schema: "public",
				table: "league_members",
				filter: `league_id=eq.${leagueId}`,
			},
			() => {
				// Ready flags changed - refresh the Multiplayer page live, and if
				// everyone is done bidding during an FA auction, the runner's
				// client resolves the round. If someone toggled auto-draft while
				// the draft is paused on their pick, the runner rolls it forward.
				// Suppressed mid-import (see above).
				if (!isImportingSnapshot()) {
					void toUI("realtimeUpdate", [["multiplayer"]]);
					void maybeAutoResolveFa(leagueId);
					void continueDraftIfAuto(leagueId);
				}
			},
		)
		.on(
			"postgres_changes",
			{
				event: "UPDATE",
				schema: "public",
				table: "leagues",
				filter: `id=eq.${leagueId}`,
			},
			(change) => {
				// Keep the Multiplayer page live for every leagues change -
				// checkpoint advances, playoff slices (playoffs_active/alive), draft
				// state, sim_open, etc. The sim-event handler imports snapshots but
				// does NOT refresh this view, so this is the refresh that shows the
				// playoff panel and alive/spectator state on non-runner clients.
				// EXCEPT while this client is importing a snapshot: a view refresh
				// then would hit the mid-recreation league DB and can corrupt it
				// ("Undefined season"). The import fires a full refresh at the end.
				if (!isImportingSnapshot()) {
					void toUI("realtimeUpdate", [["multiplayer"]]);
				}
				// A human submitted a draft pick - the runner applies it and
				// continues auto-picking to the next human.
				if ((change.new as any)?.draft_pending_pid !== null) {
					void onDraftPending(leagueId);
				}
				// Keep the runner's draft safety-net poll running whenever the draft
				// is live (self-stops for non-runners / when the draft ends).
				if ((change.new as any)?.draft_active) {
					ensureDraftReconcile(leagueId);
				}
			},
		)
		.on("broadcast", { event: "live_day" }, (msg) => {
			// Phase 8a: the runner broadcast a simulated day's scores. Ephemeral -
			// just show them; the checkpoint snapshot remains authoritative.
			void toUI("updateLocal", [{ mpLiveScores: msg.payload }]);
		})
		.on("broadcast", { event: "live_done" }, () => {
			void toUI("updateLocal", [{ mpLiveScores: undefined }]);
		})
		.on("broadcast", { event: "live_play" }, (msg) => {
			// Phase 8b: the runner is watching a game play-by-play; accumulate the
			// plays into a spectator feed. State lives here (the worker) so we can
			// append across broadcasts, then push the whole feed to the UI.
			const p = msg.payload as {
				gid: number;
				teams: MpLiveGame["teams"];
				series?: MpLiveGame["series"];
				plays: MpLiveGame["plays"];
			};
			if (!spectatorLiveGame || spectatorLiveGame.gid !== p.gid) {
				spectatorLiveGame = {
					gid: p.gid,
					teams: p.teams,
					series: p.series,
					plays: [],
					ended: false,
				};
			}
			spectatorLiveGame.plays.push(...p.plays);
			// Cap the feed so a long game can't grow unbounded
			if (spectatorLiveGame.plays.length > 400) {
				spectatorLiveGame.plays = spectatorLiveGame.plays.slice(-400);
			}
			void toUI("updateLocal", [{ mpLiveGame: { ...spectatorLiveGame } }]);
		})
		.on("broadcast", { event: "live_game_end" }, () => {
			if (spectatorLiveGame) {
				spectatorLiveGame.ended = true;
				void toUI("updateLocal", [{ mpLiveGame: { ...spectatorLiveGame } }]);
			}
		})
		.subscribe();

	await syncNow(leagueId);

	// Ledger backfill for leagues started before Phase 2 existed
	void ensureAssetsSeeded(leagueId);
};

const onDraftPending = async (leagueId: string) => {
	try {
		await processDraftPick(leagueId, {});
	} catch (error) {
		console.error("Draft pick processing failed:", error);
	}
};

const onOfferChange = async (leagueId: string, change: any) => {
	if (connectedLeagueId !== leagueId || isImportingSnapshot()) {
		return;
	}

	// Toast when a new offer lands for my team
	try {
		if (change.eventType === "INSERT" && change.new?.status === "pending") {
			const session = await getClient().auth.getSession();
			const userId = session.data.session?.user.id;
			const { data: me } = await getClient()
				.from("league_members")
				.select()
				.eq("league_id", leagueId)
				.eq("user_id", userId)
				.single();
			if (me && me.tid === change.new.to_tid) {
				logEvent({
					type: "info",
					text: `You received a trade offer! View it on the <a href="${helpers.leagueUrl(["multiplayer"])}">Multiplayer</a> page.`,
					saveToDb: false,
					showNotification: true,
				});
			}
		}
	} catch {
		// Toast is cosmetic; never break sync over it
	}

	await toUI("realtimeUpdate", [["multiplayer"]]);
};
